import i18n from '@/i18n';
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { findTagByName, normalizeTagName } from '@/lib/tagName';

export interface Tag {
  id: string;
  account_id: string;
  name: string;
  color: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export function useTags() {
  const { user } = useAuth();
  const [tags, setTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTags = useCallback(async () => {
    if (!user?.account?.id || !supabase) return;

    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('account_tags')
        .select('*')
        .eq('account_id', user.account.id)
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;
      setTags(data || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [user?.account?.id]);

  /**
   * Busca no banco (e não na lista em memória) porque outra aba, um fluxo ou um
   * colega podem ter criado a tag depois do último fetch — e é justamente essa
   * janela que gerava a linha duplicada.
   */
  const findExistingTag = async (name: string): Promise<Tag | null> => {
    if (!user?.account?.id || !supabase) return null;

    const { data, error: lookupError } = await supabase
      .from('account_tags')
      .select('*')
      .eq('account_id', user.account.id);

    if (lookupError) throw lookupError;
    return findTagByName<Tag>(data || [], name) ?? null;
  };

  /**
   * Devolve a tag existente quando o nome já está cadastrado em qualquer caixa,
   * em vez de criar uma segunda linha para "VIP" ao lado de "vip".
   */
  const createTag = async (tag: Omit<Tag, 'id' | 'account_id' | 'created_at' | 'updated_at'>) => {
    if (!user?.account?.id || !supabase) return null;

    const name = normalizeTagName(tag.name);
    if (!name) return null;

    try {
      const existing = await findExistingTag(name);
      if (existing) {
        setTags(prev => (prev.some(t => t.id === existing.id) ? prev : [existing, ...prev]));
        return existing;
      }

      const { data, error: createError } = await supabase
        .from('account_tags')
        .insert({
          account_id: user.account.id,
          ...tag,
          name
        })
        .select()
        .single();

      if (createError) throw createError;

      setTags(prev => [data, ...prev]);
      return data;
    } catch (err: any) {
      setError(err.message);
      return null;
    }
  };

  const updateTag = async (id: string, updates: Partial<Tag>) => {
    if (!supabase) return false;

    try {
      const patch = { ...updates };

      // Renomear também normaliza: sem isso, editar "vip" para "VIP" pelo painel
      // de tags descolaria a linha das tags já gravadas nos contatos.
      if (typeof patch.name === 'string') {
        const name = normalizeTagName(patch.name);
        if (!name) return false;

        const existing = await findExistingTag(name);
        if (existing && existing.id !== id) {
          setError(i18n.t('settings:tags.duplicate'));
          return false;
        }

        patch.name = name;
      }

      const { error: updateError } = await supabase
        .from('account_tags')
        .update({ ...patch, updated_at: new Date().toISOString() })
        .eq('id', id);

      if (updateError) throw updateError;

      setTags(prev => prev.map(t => t.id === id ? { ...t, ...patch } : t));
      return true;
    } catch (err: any) {
      setError(err.message);
      return false;
    }
  };

  const deleteTag = async (id: string) => {
    if (!supabase || !user?.account?.id) return false;

    try {
      const tagToDelete = tags.find(t => t.id === id);

      if (tagToDelete) {
        const normalizedName = tagToDelete.name.trim().toLowerCase();
        const possibleNames = Array.from(
          new Set([normalizedName, tagToDelete.name, tagToDelete.name.trim()])
        );

        const { data: contactsWithTag, error: fetchContactsError } = await supabase
          .from('account_contacts')
          .select('id, tags')
          .eq('account_id', user.account.id)
          .overlaps('tags', possibleNames);

        if (fetchContactsError) throw fetchContactsError;

        const updates = (contactsWithTag || []).map(async (contact) => {
          const currentTags = (contact.tags as string[]) || [];
          const newTags = currentTags.filter(
            (t) => t.trim().toLowerCase() !== normalizedName
          );

          if (newTags.length === currentTags.length) return null;

          return supabase!
            .from('account_contacts')
            .update({ tags: newTags })
            .eq('id', contact.id)
            .eq('account_id', user.account!.id);
        });

        await Promise.all(updates);

        const { data: campaignsWithTag, error: fetchCampaignsError } = await supabase
          .from('account_campaigns')
          .select('id, tags')
          .eq('account_id', user.account.id)
          .overlaps('tags', possibleNames);

        if (fetchCampaignsError) throw fetchCampaignsError;

        const campaignUpdates = (campaignsWithTag || []).map(async (campaign) => {
          const currentCampaignTags = (campaign.tags as string[]) || [];
          const newCampaignTags = currentCampaignTags.filter(
            (t) => t.trim().toLowerCase() !== normalizedName
          );

          if (newCampaignTags.length === currentCampaignTags.length) return null;

          return supabase!
            .from('account_campaigns')
            .update({ tags: newCampaignTags })
            .eq('id', campaign.id)
            .eq('account_id', user.account!.id);
        });

        await Promise.all(campaignUpdates);
      }

      const { error: deleteError } = await supabase
        .from('account_tags')
        .delete()
        .eq('id', id);

      if (deleteError) throw deleteError;

      setTags(prev => prev.filter(t => t.id !== id));
      return true;
    } catch (err: any) {
      setError(err.message);
      return false;
    }
  };

  useEffect(() => {
    fetchTags();
  }, [fetchTags]);

  return {
    tags,
    loading,
    error,
    fetchTags,
    createTag,
    updateTag,
    deleteTag
  };
}
